import { Subject } from 'rxjs';
import * as URI from 'urijs';

// types
import {
    IUbiConnectConfig,
    IUbisoftConnectService
} from './types';

// constants
import { externalLoginOptionType, AUTH_URL_POPUP } from './constants';

// utils
import {
    calculatePopupSize,
    getConnectEndpoint,
} from './utils';

// service
import ubisoftOverlayService from 'modules/ubisoft-overlay-service';


export class UbisoftConnectService implements IUbisoftConnectService {
    private static websiteAbsolutePath: string;
    public sizeSubject: Subject<number> = new Subject();
    private config: IUbiConnectConfig;
    private loginCallback: () => void;

    public static configure = ({ websiteAbsolutePath }: {
        websiteAbsolutePath: string
    }): void => {
        UbisoftConnectService.websiteAbsolutePath = websiteAbsolutePath;
    };

    public init(config: IUbiConnectConfig) {
        this.config = this.prepareConfig(config);

        window.addEventListener('message', this.receiveUbisoftConnectIframeMessage, false);
    }

    private isLoginMessage(event: MessageEvent): boolean {
        const isCorrectGenomeId = event.data.genomeId === this.config.genomeId;
        const isCorrectTopic = event.data.topic === this.config.topicOfPostMessage;
        const isCorrectApplicationId = event.data.applicationId === this.config.applicationId;

        return isCorrectGenomeId && isCorrectTopic && isCorrectApplicationId;
    }

    private isSizeChangedMessage(event: MessageEvent): number | false {
        let data: { connectRoute: string, windowHeight: number };

        try {
            data = JSON.parse(event.data);
        } catch (error) {
            return false;
        }

        return (data.connectRoute && data.windowHeight) ? data.windowHeight : false;
    }

    private receiveUbisoftConnectIframeMessage = (event: MessageEvent) => {
        const isLoginMessage = this.isLoginMessage(event);
        const isSizeChangedMessage = this.isSizeChangedMessage(event);

        if (isLoginMessage) {
            ubisoftOverlayService.reInit();
            return this.loginCallback();
        }

        if (isSizeChangedMessage) {
            return this.sizeSubject.next(isSizeChangedMessage);
        }
    };

    private prepareConfig = (config: IUbiConnectConfig): IUbiConnectConfig => {
        return {
            ...config,
            nextUrl: decodeURIComponent(this.createNextURL(config.nextUrl))
        };
    };

    private createNextURL = (nextUrl: string) => {
        return new URI(UbisoftConnectService.websiteAbsolutePath).filename(nextUrl).href();
    };

    private generateConnectNextUrl = (hasTPCEnabled: boolean) => {
        return hasTPCEnabled
            ? this.config.nextUrl
            : `${getConnectEndpoint(this.config.envName)}/${AUTH_URL_POPUP}?appId=${
                this.config.applicationId
            }&genomeId=${this.config.genomeId}&lang=en-GB`;
    };

    public registerLoginCallback(loginCallback: () => void) {
        this.loginCallback = loginCallback;
    }

    public getLoginPopIn = (externalLogin: string, languageCode?: string, size?: { width?: string, height?: string }) => {
        const iframe: HTMLIFrameElement = document.createElement('iframe');

        iframe.sandbox.add(
            'allow-same-origin',
            'allow-forms',
            'allow-popups-to-escape-sandbox',
            'allow-popups',
            'allow-scripts');

        const url = new URI(getConnectEndpoint(this.config.envName))
            .segment([
                externalLogin
            ])
            .query({
                appId: this.config.applicationId,
                genomeId: this.config.genomeId,
                lang: languageCode || this.config.lang,
                nextUrl: decodeURIComponent(this.config.nextUrl)
            })
            .href();

        iframe.setAttribute('src', url);

        iframe.classList.add('iFrame');
        iframe.setAttribute('width', size && size.width ? size.width : '480');
        iframe.setAttribute('height', size && size.height ? size.height : '720');
        return iframe;
    };

    public openLoginPopUp(
        languageCode: string,
        externalLogin: externalLoginOptionType,
        align?: string,
        hasTPCEnabled: boolean = true
    ) {
        const connectNextUrl: string = this.generateConnectNextUrl(hasTPCEnabled);

        const url = new URL(`${getConnectEndpoint(this.config.envName)}/${externalLogin}`);
        url.searchParams.set('appId', this.config.applicationId);
        url.searchParams.set('genomeId', this.config.genomeId);
        url.searchParams.set('lang', languageCode || this.config.lang);
        url.searchParams.set('nextUrl', connectNextUrl);

        const target = 'Ubisoft Connect';
        const popupSize = calculatePopupSize(align);
        const features = `width=${popupSize.width},height=${popupSize.height},left=${popupSize.left},top=${
            popupSize.top
        },toolbar=no,status=no`;

        return window.open(url.toString(), target, features);
    }
}

const ubisoftConnectService = new UbisoftConnectService();

export default ubisoftConnectService;
